import { AlertStatus, useToast, UseToastOptions } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { AppError } from '../../framework/models/errors';
import { TypeUtils } from '../../framework/utils/UtilityTypes';

type ToastParam = {
  errorOrMessage: AppError | string;
  statusOrToastOptions: AlertStatus | UseToastOptions | undefined;
};

const isUseToastOptions = (
  obj: AlertStatus | UseToastOptions | undefined
): obj is UseToastOptions => typeof obj === 'object';

const defaultOptions: UseToastOptions = {
  position: 'bottom',
  duration: 5000,
  isClosable: true,
};

const useToastHook = () => {
  const [state, setState] = useState<ToastParam>();
  const toast = useToast();

  // errorOrMessage:
  //   - AppError -> AppError.severity で status を分岐する。ただし statusOrToastOptions が指定されていればそれで上書き。
  //   - string   -> status は デフォルトで 'info'。ただし statusOrToastOptions が指定されていればそれで上書き。

  useEffect(() => {
    if (state) {
      const { errorOrMessage, statusOrToastOptions } = state;

      let obj: UseToastOptions | undefined;

      if (errorOrMessage instanceof AppError) {
        obj =
          // 不意に「ユーザ認証に失敗しました」(401)エラーが出る問題への対処
          // (そのまま継続利用できるので一時的なもの？)
          // keycloak との不整合であればログイン画面に行くはずなので
          // アプリ内で 401 を受け取った場合は握りつぶす
          errorOrMessage.status === 401
            ? undefined
            : {
                description: errorOrMessage.message,
                status:
                  errorOrMessage.severity === 'WARNING' ? 'warning' : 'error',
                ...defaultOptions,
              };
      } else {
        obj = {
          description: errorOrMessage,
          status: 'info',
          ...defaultOptions,
        };
      }

      // この時点で 401 以外は obj !== undefined が確約されている

      if (TypeUtils.isNil(obj)) {
        // do nothing
      } else {
        if (isUseToastOptions(statusOrToastOptions)) {
          obj = {
            ...obj,
            ...statusOrToastOptions,
          };
        } else if (statusOrToastOptions) {
          obj = {
            ...obj,
            status: statusOrToastOptions || 'info',
          };
        }
        toast(obj);
      }
    }
  }, [state, toast]);

  return {
    toastState: state,
    toast: (
      errorOrMessage: AppError | string,
      statusOrToastOptions?: AlertStatus | UseToastOptions
    ) => setState({ errorOrMessage, statusOrToastOptions }),
  };
};
export default useToastHook;
